import { useEffect, useState, useRef } from "react";
import "./GpsServices.css";
import GpsImg from "../../assets/images/gps.png";
import ContactForm from "../../components/ContactUs/ContactUs";
import {
  MapPin,
  Navigation,
  Truck,
  Shield,
  DollarSign,
  Clock,
  AlertTriangle,
  BarChart3,
  CheckCircle,
  Smartphone,
  Zap,
} from "lucide-react";

const GPSServices = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  // Inline scroll to top functionality and trigger animations
  useEffect(() => {
    window.scrollTo(0, 0);

    // Trigger hero animations
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    // Setup intersection observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(
              (prev) => new Set([...prev, entry.target.dataset.section])
            );
          }
        });
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    // Observe all sections
    const sections = document.querySelectorAll("[data-section]");
    sections.forEach((section) => {
      if (observerRef.current) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const benefits = [
    {
      icon: Shield,
      title: "Improved Safety",
      description:
        "Enhanced driver safety through real-time monitoring and emergency response capabilities.",
    },
    {
      icon: DollarSign,
      title: "Minimize Fuel Costs",
      description:
        "Optimize routes and reduce fuel consumption with intelligent tracking and analytics.",
    },
    {
      icon: BarChart3,
      title: "Lower Operational Costs",
      description:
        "Streamline operations and reduce overhead through efficient fleet management.",
    },
    {
      icon: Zap,
      title: "Increased Productivity",
      description:
        "Boost productivity with optimized routing and real-time fleet coordination.",
    },
    {
      icon: AlertTriangle,
      title: "Theft Recovery",
      description:
        "Advanced security features and tracking capabilities for asset protection.",
    },
    {
      icon: Clock,
      title: "Instant Alerts",
      description:
        "Real-time notifications for critical events and fleet status updates.",
    },
  ];

  const additionalBenefits = [
    "Reduces Time Spent on Road",
    "Improved Administration",
    "Improved Lifecycle Management",
    "Real-time vehicle tracking and monitoring",
    "Comprehensive reporting and analytics",
    "Customizable alerts and notifications",
    "Integration with existing business systems",
    "Scalable solutions for fleets of any size",
  ];

  return (
    <div className="gps-services-main-container">
      {/* Floating Background Elements */}
      <div className="gps-services-floating-background">
        <div className="gps-services-floating-element gps-services-element-1"></div>
        <div className="gps-services-floating-element gps-services-element-2"></div>
        <div className="gps-services-floating-element gps-services-element-3"></div>
      </div>

      {/* Hero Section */}
      <section className="gps-services-hero-wrapper">
        <div className="gps-services-content-width">
          <div className="gps-services-hero-layout">
            <div className="gps-services-hero-content">
              <div
                className={`gps-services-tagline ${
                  isVisible ? "gps-services-slide-down" : ""
                }`}
              >
                <span className="gps-services-tagline-text">
                  Smart tracking solutions
                </span>
                <span className="gps-services-tagline-highlight">
                  for modern fleets.
                </span>
              </div>
            </div>
            <div className="gps-services-hero-main">
              <div
                className={`gps-services-hero-icon ${
                  isVisible ? "gps-services-scale-in" : ""
                }`}
              >
                <div className="gps-services-pulse-ring"></div>
                <Navigation className="gps-services-hero-navigation-icon" />
              </div>
              <div className="gps-services-hero-text">
                <h1
                  className={`gps-services-hero-title ${
                    isVisible ? "gps-services-slide-up" : ""
                  }`}
                >
                  GPS SERVICES
                </h1>
                <p
                  className={`gps-services-hero-description ${
                    isVisible ? "gps-services-fade-in" : ""
                  }`}
                >
                  Our customers expect smart tracking solutions. GPS services
                  lets you offer advanced fleet tracking software with your
                  brand front and center. Our system is configurable so you can
                  tailor it to your customers' needs and this is possible with
                  our partners GpsGate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Build Your Business Section */}
      <section
        className="gps-services-business-section"
        data-section="business"
      >
        <div className="gps-services-content-width">
          <div className="gps-services-business-content">
            <h2
              className={`gps-services-business-title ${
                visibleSections.has("business") ? "gps-services-zoom-in" : ""
              }`}
            >
              Build your business with us
            </h2>
            <div
              className={`gps-services-business-description ${
                visibleSections.has("business") ? "gps-services-fade-in-up" : ""
              }`}
            >
              <p>
                Customer relationships matter the most. We let you focus on
                providing stellar services to your customers while we handle the
                software development. You help your customers improve fleet
                efficiency, stay safe, and meet regulations – and GpsGate stays
                in the background ready to help your business with anything you
                need.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GPS Tracking Image Section */}
      <section className="gps-services-image-section" data-section="image">
        <div className="gps-services-content-width">
          <div className="gps-services-center-align">
            <h2
              className={`gps-services-image-heading ${
                visibleSections.has("image") ? "gps-services-slide-up" : ""
              }`}
            >
              Advanced Fleet Tracking Solutions
            </h2>
            <p
              className={`gps-services-image-description ${
                visibleSections.has("image") ? "gps-services-fade-in" : ""
              }`}
            >
              Experience the power of real-time GPS tracking with comprehensive
              fleet management capabilities.
            </p>
            <div
              className={`gps-services-image-container ${
                visibleSections.has("image") ? "gps-services-scale-in" : ""
              }`}
            >
              <img
                src={GpsImg || "/placeholder.svg"}
                alt="GPS fleet tracking dashboard showing real-time vehicle locations, routes, and analytics"
                className="gps-services-showcase-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        className="gps-services-benefits-section"
        data-section="benefits"
      >
        <div className="gps-services-content-width">
          <div className="gps-services-center-align">
            <h2
              className={`gps-services-benefits-title ${
                visibleSections.has("benefits") ? "gps-services-zoom-in" : ""
              }`}
            >
              Benefits
            </h2>
            <p
              className={`gps-services-benefits-description ${
                visibleSections.has("benefits") ? "gps-services-fade-in" : ""
              }`}
            >
              Transform your fleet operations with comprehensive GPS tracking
              benefits and proven results.
            </p>
          </div>

          <div className="gps-services-benefits-grid">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className={`gps-services-benefit-card ${
                    visibleSections.has("benefits")
                      ? "gps-services-slide-up"
                      : ""
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="gps-services-benefit-card-header">
                    <div className="gps-services-benefit-card-icon">
                      <Icon className="gps-services-medium-icon" />
                    </div>
                    <h3 className="gps-services-benefit-card-title">
                      {benefit.title}
                    </h3>
                  </div>
                  <div className="gps-services-benefit-card-body">
                    <p className="gps-services-benefit-card-description">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Benefits Section */}
      <section
        className="gps-services-additional-benefits-section"
        data-section="additional"
      >
        <div className="gps-services-content-width">
          <div className="gps-services-center-align">
            <h2
              className={`gps-services-additional-benefits-title ${
                visibleSections.has("additional") ? "gps-services-zoom-in" : ""
              }`}
            >
              Additional Key Benefits
            </h2>
            <p
              className={`gps-services-additional-benefits-description ${
                visibleSections.has("additional") ? "gps-services-fade-in" : ""
              }`}
            >
              Comprehensive fleet management capabilities designed to enhance
              productivity and streamline operations.
            </p>
          </div>

          <div className="gps-services-additional-benefits-grid">
            {additionalBenefits.map((benefit, index) => (
              <div
                key={index}
                className={`gps-services-additional-benefit-item ${
                  visibleSections.has("additional")
                    ? "gps-services-slide-right"
                    : ""
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CheckCircle className="gps-services-additional-benefit-check-icon" />
                <span className="gps-services-additional-benefit-text">
                  {benefit}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section
        className="gps-services-partnership-section"
        data-section="partnership"
      >
        <div className="gps-services-content-width">
          <div className="gps-services-partnership-content">
            <h2
              className={`gps-services-partnership-title ${
                visibleSections.has("partnership") ? "gps-services-zoom-in" : ""
              }`}
            >
              Powered by GpsGate Partnership
            </h2>
            <p
              className={`gps-services-partnership-description ${
                visibleSections.has("partnership")
                  ? "gps-services-fade-in-up"
                  : ""
              }`}
            >
              Our collaboration with GpsGate ensures you receive cutting-edge
              GPS tracking technology with reliable support and continuous
              innovation. This partnership allows us to deliver scalable,
              customizable solutions that grow with your business needs.
            </p>
            <div
              className={`gps-services-partnership-highlights ${
                visibleSections.has("partnership")
                  ? "gps-services-stagger-in"
                  : ""
              }`}
            >
              <div
                className="gps-services-partnership-highlight"
                style={{ animationDelay: "0.1s" }}
              >
                <MapPin className="gps-services-partnership-icon" />
                <span>Global GPS Coverage</span>
              </div>
              <div
                className="gps-services-partnership-highlight"
                style={{ animationDelay: "0.2s" }}
              >
                <Truck className="gps-services-partnership-icon" />
                <span>Fleet Management</span>
              </div>
              <div
                className="gps-services-partnership-highlight"
                style={{ animationDelay: "0.3s" }}
              >
                <Smartphone className="gps-services-partnership-icon" />
                <span>Mobile Integration</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gps-services-cta-section" data-section="cta">
        <div className="gps-services-content-width gps-services-center-align">
          <h2
            className={`gps-services-cta-title ${
              visibleSections.has("cta") ? "gps-services-zoom-in" : ""
            }`}
          >
            Ready to Transform Your Fleet Management?
          </h2>
          <p
            className={`gps-services-cta-description ${
              visibleSections.has("cta") ? "gps-services-fade-in" : ""
            }`}
          >
            Get started with our advanced GPS tracking solutions and experience
            the benefits of smart fleet management with GpsGate technology.
          </p>
          <div
            className={`gps-services-cta-buttons ${
              visibleSections.has("cta") ? "gps-services-bounce-in" : ""
            }`}
          >
            <button
              className="gps-services-primary-button"
              onClick={() => setShowContact(true)}
            >
              <Navigation className="gps-services-small-icon" />
              Get GPS Solutions
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContact && (
        <div
          className="modal-overlay gps-services-modal-fade-in"
          onClick={() => setShowContact(false)}
        >
          <div
            className="modal-content gps-services-modal-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowContact(false)}
            >
              ✕
            </button>
            <ContactForm />
          </div>
        </div>
      )}
    </div>
  );
};

export default GPSServices;
